/**
 * Zar atma durumu (global mini store).
 *
 * Sheet'in herhangi bir yerindeki HER sayı tiklanabilir olmalıdır. Bunu
 * prop drilling ile degil, kucuk bir global store ile cozuyoruz: boylece
 * bilesenler sadece `rollCheck(edge, etiket)` cagirir, overlay her zaman
 * ustte gelir.
 *
 * Zar motoru (`@tttrpg/rules`) BAGIMSIZ oldugu icin buradaki tum mantik
 * yalnizca goruntulemedir: atisi motor yapar, bu dosya yalnizca sonucu
 * ekrana tasir.
 */

import {
  checkSuccess,
  type D20Result,
  type FormulaResult,
  parseDiceFormula,
  rollD20,
  rollFormula,
  seededRng,
} from '@tttrpg/rules'
import { create } from 'zustand'

export type RollKind = 'd20' | 'formula' | 'attack'

export type Outcome = {
  dc: number
  success: boolean
  grade: 'critical-success' | 'success' | 'fumble' | 'failure'
  margin: number
}

export interface PendingRoll {
  id: number
  kind: RollKind
  label: string
  subtitle: string
  /**
   * TOHUM — atışın doğrulanabilir olmasını sağlayan sayı.
   *
   * Neden saklanıyor: GM ekranında atış yayınlanırken tohum da gider.
   * Böylece "zarı değiştirdin" iddiası kanıtlanabilir — aynı tohum aynı
   * sonucu verir. Tohum üretilmeden yayınlansaydı bu iddia doğrulanamaz ve
   * yayın özellikle masada şüphe doğururdu.
   */
  seed: number
  /** d20 testleri icin */
  d20: D20Result | null
  /** Zar formulu (hasar, Yasma, olum kurtarma vs.) */
  formula: FormulaResult | null
  attack: {
    attackBonus: number
    damageDice: string
    damageBonus: number
    damageType: string
    targetAc: number | null
    hit: boolean | null
    damageTotal: number
  } | null
  /** DC'ye karsi basari kontrolu (d20 testlerinde dolu) */
  outcome: Outcome | null
  /** Ne zaman atildi (gecmis siralamasi icin) */
  at: number
}

const HISTORY_LIMIT = 25

interface RollState {
  /** Ekranda ANDA gorunen atislar (kuyruk). En fazla biri gorunur. */
  queue: PendingRoll[]
  /** Kalici gecmis: overlay kapansa da kaybolmaz. */
  history: PendingRoll[]
  push: (roll: Omit<PendingRoll, 'id' | 'at'>) => void
  // Not: `seed` alanı zorunlu — bkz. `push` açıklaması.
  dismiss: (id: number) => void
  dismissAll: () => void
  clearHistory: () => void
}

let counter = 0

/**
 * YENİ, ÇARPIŞMASIZ BİR TOHUM.
 *
 * Neden `Math.random()` değil: tohum kaydedilip sonradan doğrulanacağı için
 * çakışmamalı. İki atış aynı tohumu alırsa iki farklı oyuncu aynı sonucu
 * "hile" sanır. `Date.now()` tek başına da yetmez — aynı milisaniyedeki iki
 * atış (masada bu oluyor) aynı tohumu alırdı.
 *
 * `crypto.getRandomValues` tarayıcıda kriptografik rastgelelik verir; yine de
 * zaman damgası ile karıştırılır ki iki cihaz aynı anda attığında bile
 * farklı tohum çıksın.
 */
export function nextSeed(): number {
  const buffer = new Uint32Array(1)
  crypto.getRandomValues(buffer)
  return (buffer[0]! ^ Date.now()) >>> 0
}

export const useRollStore = create<RollState>((set) => ({
  queue: [],
  history: [],
  // `seed` CAGIRAN taraftan gelir, burada üretilmez. Neden: tohum atışı
  // ÜRETMEK zorundadır (`seededRng(seed)` ile). Tohum atıştan sonra
  // üretilseydi saklanan tohum sonucu vermezdi ve doğrulama iddiası
  // havada kalırdı — GM'ye yayınlanan sayı kanıt olmazdı.
  push: (roll) =>
    set((s) => {
      const entry: PendingRoll = { ...roll, id: ++counter, at: Date.now() }
      return {
        queue: [...s.queue, entry],
        history: [entry, ...s.history].slice(0, HISTORY_LIMIT),
      }
    }),
  dismiss: (id) => set((s) => ({ queue: s.queue.filter((r) => r.id !== id) })),
  dismissAll: () => set({ queue: [] }),
  clearHistory: () => set({ history: [] }),
}))

// ---------------------------------------------------------------------------
// Zar formulu yardimcilari
// ---------------------------------------------------------------------------

/** Bozuk formulu sessizce bos formule cevirir (kullaniciya hata gostermeyelim). */
function safeParse(formula: string) {
  try {
    return parseDiceFormula(formula)
  } catch {
    return { terms: [], flat: 0 }
  }
}

/** "1d8" -> "2d8" (natur 20'de hasar ikiye katlanir). */
function doubleDice(dice: string): string {
  return dice.replace(/(\d*)d(\d+)/, (_m, count: string, sides: string) => {
    return `${Number(count || '1') * 2}d${sides}`
  })
}

export function signed(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`
}

// ---------------------------------------------------------------------------
// Atis tetikleyicileri
// ---------------------------------------------------------------------------

/**
 * Bir d20 testi atar. `dc` verilirse basari kontrolu de yapilir — masada
 * "DC 15'e karsi Stealth" denildiginde sonucu soylemek yeterli olur.
 */
export function rollCheck(
  label: string,
  modifier: number,
  opts: { subtitle?: string; dc?: number | null } = {},
): void {
  const seed = nextSeed()
  const result = rollD20('normal', modifier, seededRng(seed))
  useRollStore.getState().push({
    kind: 'd20',
    label,
    subtitle: opts.subtitle ?? `1d20 ${signed(modifier)}`,
    seed,
    d20: result,
    formula: null,
    attack: null,
    outcome: opts.dc != null ? { dc: opts.dc, ...checkSuccess(result, opts.dc) } : null,
  })
}

/** Bir zar formulu atar (hasar, Yaşama, ölüm kurtarma vs.). */
export function rollFormulaAndPush(label: string, formula: string, subtitle?: string): void {
  const seed = nextSeed()
  const parsed = safeParse(formula)
  const result = rollFormula(parsed, seededRng(seed))
  useRollStore.getState().push({
    kind: 'formula',
    label,
    subtitle: subtitle ?? formula,
    seed,
    d20: null,
    formula: result,
    attack: null,
    outcome: null,
  })
}

/**
 * Tam bir saldiri: isabet zarı + hasar. `targetAc` verilirse kırılma
 * gösterilir ve hasar yalnızca isabet edildiğinde atılır.
 */
export function rollAttack(opts: {
  label: string
  attackBonus: number
  damageDice: string
  damageBonus: number
  damageType: string
  targetAc: number | null
}): void {
  const seed = nextSeed()
  // Isabet ve hasar AYNI tohum akisindan: bagimsiz zarlar, ama ikisi de
  // seed ile uretilir ve saklanan tek tohum atisin tamamini yeniden uretir.
  const rng = seededRng(seed)
  const attack = rollD20('normal', opts.attackBonus, rng)
  const hit = opts.targetAc == null ? null : attack.total >= opts.targetAc

  let formula: FormulaResult | null = null
  let damageTotal = 0
  if (hit !== false) {
    // Natur 20'de hasar zarı ikiye katlanır
    const dice = attack.isCritical ? doubleDice(opts.damageDice) : opts.damageDice
    const parsed = safeParse(dice)
    formula = rollFormula({ terms: parsed.terms, flat: parsed.flat + opts.damageBonus }, rng)
    damageTotal = formula.total
  }

  useRollStore.getState().push({
    kind: 'attack',
    label: opts.label,
    subtitle: `Saldırı ${signed(opts.attackBonus)} · ${opts.damageDice}${signed(opts.damageBonus)} ${opts.damageType}`,
    seed,
    d20: attack,
    formula,
    attack: {
      attackBonus: opts.attackBonus,
      damageDice: opts.damageDice,
      damageBonus: opts.damageBonus,
      damageType: opts.damageType,
      targetAc: opts.targetAc,
      hit,
      damageTotal,
    },
    outcome: null,
  })
}
